import {
  action,
  computed,
  makeObservable,
  onBecomeObserved,
  reaction,
} from "mobx"

import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { MassPayoutsCollectionFilter } from "@src/pages/massPayouts/crypto/classess/MassPayoutsCryptoCollection"
import { catchErrors, massPayoutsApi } from "@src/shared/api"
import {
  MassPayoutPayoutResponse,
  MassPayoutPayoutResponseStatusEnum,
  Pagination,
} from "@src/shared/api/generated"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"
import { filtersPresets } from "@src/shared/componemts/TableFilter/classes/constants"

export type PayoutsCollectionLoadRequest = {
  date?: {
    dateFrom?: string
    dateTo?: string
  }
  externalId?: string
  id: string
  order?: "asc" | "desc"
  page?: number
  status?: "completed" | "failed" | "new" | "pending"
}

export type MassPayoutPayoutResponseWithExplorerUrl = Omit<
  MassPayoutPayoutResponse,
  "data"
> & { data: MassPayoutPayoutResponse["data"] & { tx_explorer_url?: string } }

export class PayoutsCryptoCollection extends loading(
  ["fetch"],
  CollectionWithPagination<MassPayoutPayoutResponse>,
) {
  private currencies = GlobalCurrenciesCollection()

  constructor(id: string) {
    super()

    makeObservable(this, {
      failed: computed,
      fetch: action.bound,
      filter: computed,
      forDownload: action.bound,
      id: computed,
    })

    onBecomeObserved(this, "isLoading", () => this.load({ id }))

    reaction(
      () => this.filter.state,
      (v) => v && this.load(v),
    )
  }

  async fetch(req: PayoutsCollectionLoadRequest) {
    try {
      if (!req.id) {
        req.id = this.id
      }

      const { data: { data } = {} } = await massPayoutsApi.listPayouts(
        req.id,
        req.externalId,
        req.date?.dateFrom,
        req.date?.dateTo,
        req.status,
        req.order,
        this.calcOffset(req.page),
        this.pageSize,
      )

      if (data) {
        return {
          data: data.data as MassPayoutPayoutResponse[],
          pagination: data.pagination as Pagination,
        }
      }
    } catch (error: any) {
      catchErrors(error)
    }

    return null
  }

  forDownload(payouts: MassPayoutPayoutResponse[]) {
    return payouts.map((p) => ({
      "id": p.id,
      // eslint-disable-next-line perfectionist/sort-objects
      "External ID": p.external_id,
      // eslint-disable-next-line perfectionist/sort-objects
      "Date and Time": p.created_at,
      "Status": p.status,
      // eslint-disable-next-line perfectionist/sort-objects
      "Address": p.data.address,
      "Currency": p.data.symbol,
      "Network": p.data.network,
      // eslint-disable-next-line perfectionist/sort-objects
      "Amount": p.data.amount,
      "Errors": p.error ? `${p.error.code} ${p.error.message}` : "",
    }))
  }

  get failed() {
    return this.items.filter(
      (i) => i.status === MassPayoutPayoutResponseStatusEnum.Failed,
    )
  }

  get filter() {
    return new CollectionFilters<MassPayoutsCollectionFilter>([
      CollectionFilters.createField("externalId").search({
        col: 6,
        label: "Search for External ID",
        placeholder: "External ID",
      }),
      CollectionFilters.createField("date").date({ col: 8, label: "Date" }),
      {
        ...filtersPresets.order,
        col: 4,
        label: "Select on order",
        placeholder: "Select",
      },
    ])
  }

  get id() {
    return (this.state.prevRequest as PayoutsCollectionLoadRequest).id
  }

  get withExplorerUrl(): MassPayoutPayoutResponseWithExplorerUrl[] {
    return this.items.map((item) => {
      const currency = this.currencies.find(
        (i) => i.network === item.data.network && i.symbol === item.data.symbol,
      )

      return {
        ...item,
        data: {
          ...item.data,
          tx_explorer_url: currency?.tx_explorer_url,
        },
      }
    })
  }
}
